import Vue from 'vue';
import Component from 'vue-class-component';
import { Async } from '@/utils/AsyncDecorators';
import { store } from '@/store';
import { versioneInSituStore } from '@/modules/VersioneInSitu/store/VersioneInSitu.store';
import { areaSaggioStore } from '@/modules/AreaSaggio/store/areaSaggio.store';
import { areaSaggioClasseDiametricaStore } from '@/modules/AreaSaggioClasseDiametrica/store/areaSaggioClasseDiametrica.store';
import { domainStore } from '@/modules/domains';
import { ClasseDiametricaIntersection } from '../../utils/classeDimatrica';
import { Watch } from 'vue-property-decorator';
import Row from './row.vue';

import Highcharts from 'highcharts';
import exportingInit from 'highcharts/modules/exporting';
import { printerHelper } from '@/modules/printer';
import { reportStore } from '../../store/report.store';
exportingInit(Highcharts);

@Component({ components: { Row } })
export default class Index extends Vue {

  activeTab: string = "dati";
  hcInstance = Highcharts;

  @Async({ async get() { return await versioneInSituStore.getters.current() }, default: {}, lazy: false })
  currentVersion: server.VersioneInSitu;

  @Async({ async get() { return (await areaSaggioStore.getters.getAll()).sort((a, b) => a.nome.localeCompare(b.nome)); }, default: [], lazy: false })
  areeSaggio: server.areaSaggio[];

  @Async({ async get() { return await areaSaggioClasseDiametricaStore.getters.getAll() }, default: [], lazy: false })
  areeSaggioClasseDiametrica: server.areaSaggioClasseDiametrica[];

  @Async({ async get() { return await await domainStore.getters.specieArboree(); }, default: [], lazy: false })
  specieArboree: domains.domini[];

  @Watch('filter.text')
  change(n, o) { this.showsize = 20; }
  showsize: number = 20;

  get areeSaggioAutocomplete() {
    if (this.areeSaggio.length <= 0) return [];
    return this.areeSaggio.map((m) => {
      return {
        key: m.id,
        value: m.id,
        description: m.nome,
      } as server.autocomplete
    })
  }

  get navigation() {
    if (!this.areaSaggioId) return null
    return this.areeSaggioAutocomplete.find(f => f.key === this.areaSaggioId) || {} as server.autocomplete;
  }
  set navigation(value: server.autocomplete) {
    if (value.key !== this.areaSaggioId) {
      store.actions.navigation.set_filter({ area: "areaSaggio", id: value ? value.key : null });
      reportStore.actions.set_logarithmic_regression(value ? value.key : null);
    }
  }

  get areaSaggioId() {
    return store.getters.navigation.selected("areaSaggio");
  }

  get classiDiametricheExtended(): IClasseDiametrica[] {
    if (!this.areaSaggioId) return [];

    return this.areeSaggioClasseDiametrica
      .filter(i => i.areaSaggioId === this.areaSaggioId)
      .map(m => new ClasseDiametricaIntersection(m, this.areeSaggio.find(f => f.id === m.areaSaggioId), this.specieArboree.find(f => f.id === m.specie)))
      .sort((a, b) => {
        const specieOrder = a.specieOrderIndex - b.specieOrderIndex;
        if (specieOrder !== 0)
          return specieOrder;
        return a.diametro - b.diametro;
      });
  }

  get classiDiametricheGrouped(): { diametro: number, specieArboree: { specie: string, areaBasimetrica: number }[], totale: number }[] {
    if (this.classiDiametricheExtended.length <= 0) return [];

    var reduced = this.classiDiametricheExtended.reduce((a, c) => {
      const diametro = c.diametro;
      if (!a[diametro]) {
        a[diametro] = { diametro: diametro, specieArboree: [], totale: 0 };
      }

      const specie = this.specieArboree.find(f => f.id === c.specie).value;
      let specieArborea = a[diametro].specieArboree.find(f => f.specie === specie);

      if (!specieArborea) {
        specieArborea = { specie: specie, areaBasimetrica: 0 };
        a[diametro].specieArboree.push(specieArborea);
      }
      specieArborea.areaBasimetrica += c.areaBasimetricaAdEttaro;
      a[diametro].totale += parseFloat(c.areaBasimetricaAdEttaro.toFixed(2));

      return a;
    }, []);

    return Object.values(reduced).sort((a, b) => a.diametro - b.diametro);
  }

  get classiDiametricheReduced(): { diametro: number, specieArboree: { specie: string, areaBasimetrica: number }[], totale: number }[] {
    return this.classiDiametricheGrouped.slice(0, this.showsize);
  }

  get specieArboreeSelezionateIds(): number[] {
    if (this.classiDiametricheExtended.length <= 0) return [];
    return [...new Set(this.classiDiametricheExtended.map(m => m.specie))]
  };

  get specieArboreeInserite(): domains.domini[] {
    if ((this.specieArboree || []).length <= 0 || (this.specieArboreeSelezionateIds || []).length <= 0) return [];
    const result: domains.domini[] = [];
    this.specieArboreeSelezionateIds.forEach(m => {
      const specie = this.specieArboree.find(f => f.id === m)
      if(specie) result.push(specie);
    })
    return result;
  }

  get areaBasimetricaAdEttaro(): { specie: string, areeBasimetriche: number[], totale: number }[] {
    if (this.classiDiametricheGrouped.length <= 0 || this.classiDiametricheExtended.length <= 0) return null;

    const result: {
      specie: string;
      areeBasimetriche: number[];
      totale: number;
    }[] = [];

    const _specie = [...new Set(this.classiDiametricheExtended.map(m => m.specieArborea.value))];
    const _classiDiametriche = [...new Set(this.classiDiametricheGrouped.map(m => m.diametro))];
    _specie.forEach((specie) => {
      const elem: {
        specie: string;
        areeBasimetriche: number[];
        totale: number;
      } = {
        specie: specie,
        totale: 0,
        areeBasimetriche: []
      };
      _classiDiametriche.forEach((classeDiametrica) => {
        const areaBasimetricaHa = this.classiDiametricheExtended.filter(f => f.specieArborea.value === specie 
                                                                 && f.diametro === classeDiametrica)
                                                       .map(m => m.areaBasimetricaAdEttaro).reduce((a, b) => a + b);
        const areeBasimetricheHa = parseFloat(areaBasimetricaHa.toFixed(2));
        elem.areeBasimetriche.push(areeBasimetricheHa);
        elem.totale += areeBasimetricheHa;
      })
      result.push(elem);
    });
    return result;

    const reduced = this.classiDiametricheExtended.reduce((a, c) => {
      const key = c.specieArborea.value;
      if (!a[key])
        a[key] = { specie: c.specieArborea.value, areeBasimetriche: [], totale: 0 };
      a[key].areeBasimetriche.push(c.areaBasimetricaAdEttaro);
      a[key].totale += parseFloat(c.areaBasimetricaAdEttaro.toFixed(2));
      return a;
    }, {})
    return Object.values(reduced);
  }

  get total(): number {
    if (!this.areaBasimetricaAdEttaro) return null;
    return this.areaBasimetricaAdEttaro.map(m => m.totale).reduce((a, c) => a + c);
  }

  get chartOptions() {
    if (!this.areaBasimetricaAdEttaro) return null;
    const _classiDiametriche = [...new Set(this.classiDiametricheGrouped.map(m => m.diametro))];
    const _series = this.areaBasimetricaAdEttaro.map(m => {
      return {
        name: m.specie,
        data: m.areeBasimetriche
      }
    });
    return {
      chart: {
        type: 'column'
      },
      title: {
        text: 'Aree basimetriche per classe diametrica'
      },
      xAxis: {
        title: {
          text: 'Classi diametriche'
        },
        categories: _classiDiametriche
      },
      yAxis: {
        min: 0,
        title: {
          text: 'Aree basimetriche'
        },
        stackLabels: {
          enabled: true,
          style: {
            fontWeight: 'bold',
            color: 'gray'
          }
        }
      },
      legend: {
        align: 'right',
        x: -30,
        verticalAlign: 'top',
        y: 25,
        floating: true,
        backgroundColor: 'white',
        borderColor: '#CCC',
        borderWidth: 1,
        shadow: false
      },
      tooltip: {
        headerFormat: '<b>Classe diametrica {point.x}</b><br/>',
        pointFormat: '{series.name}: {point.y}<br/>Total: {point.stackTotal}'
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          dataLabels: {
            enabled: true
          }
        }
      },
      series: _series
    }
  }

  @Watch("activeTab")
  activeTabChange() {
    const chartContainer = this.$refs.chartContainer as any;
    if (this.chartOptions && chartContainer && chartContainer.chart && chartContainer.chart.reflow) {
      this.$nextTick(() => {
        chartContainer.chart.reflow();
      });
    }
  }

  exportExcel() {
    var arrayToExport: any[][];
    arrayToExport = [];

    for (var i = 0; i < this.classiDiametricheGrouped.length; i++) {
      arrayToExport[i] = [];
      arrayToExport[this.classiDiametricheGrouped.length] = [];
      arrayToExport[this.classiDiametricheGrouped.length]["Diametro"] = "Totale";

      arrayToExport[i]["Diametro"] = this.classiDiametricheGrouped[i].diametro.toFixed(0)

      for (var j = 0; j < this.classiDiametricheGrouped[i].specieArboree.length; j++) {
        arrayToExport[i][this.classiDiametricheGrouped[i].specieArboree[j].specie] = this.classiDiametricheGrouped[i].specieArboree[j].areaBasimetrica.toFixed(2);
        arrayToExport[this.classiDiametricheGrouped.length][this.classiDiametricheGrouped[i].specieArboree[j].specie] = this.areaBasimetricaAdEttaro[j].totale.toFixed(2)
      }

      arrayToExport[i]["Totale"] = this.classiDiametricheGrouped[i].totale.toFixed(2);
      arrayToExport[this.classiDiametricheGrouped.length]["Totale"] = this.total.toFixed(2);

    }

    printerHelper.printExcel("area-basimetrica", arrayToExport, "Area Basimetrica");
  }

}